/**
 * viewerTileSkinAssets.ts — Resolves Phaser/React tile asset keys and paths
 * for the current viewer's tile skin.
 *
 * DURABLE Cosmetics infrastructure — NOT temporary. It only cares about "which
 * skin is the viewer's `ViewerTileSkin`", not where that value came from (the
 * lobby/session now supplies it via the effective-cosmetics resolver).
 *
 * 'classic' always delegates to dominoAssets.ts UNCHANGED — every viewer whose
 * resolved skin is 'classic' gets the exact same keys/paths as before.
 *
 * Every non-classic skin ('red' | 'black' | 'rainbow') shares the same on-disk
 * layout and uses two source resolutions/roles under their own texture-cache
 * key namespaces so they can never collide with classic or with each other:
 *   - hand   (hand tiles, opponent backs, deal/travel ghosts, reveal art)
 *            → /Assets/game/tiles/{skin}/tiles_328x164/ → "domino-{skin}-hand-*"
 *   - placed (tiles resting on the board)
 *            → /Assets/game/tiles/{skin}/tiles_164x82/  → "domino-{skin}-placed-*"
 */
import {
  getDominoTextureKey,
  getDominoBackTextureKey,
} from './dominoAssets';
// ViewerTileSkin is DURABLE and now permanently hosted in viewerCosmetics.ts.
import type { ViewerTileSkin } from './viewerCosmetics';

function canonical(a: number, b: number): [number, number] {
  return [Math.min(a, b), Math.max(a, b)];
}

// A non-classic skin has its own art tree. Classic is handled by dominoAssets.
type NonClassicTileSkin = Exclude<ViewerTileSkin, 'classic'>;

// ── Generic per-skin keys/paths (additive; never touches dominoAssets.ts) ──

export function getSkinHandTextureKey(skin: NonClassicTileSkin, a: number, b: number): string {
  const [lo, hi] = canonical(a, b);
  return `domino-${skin}-hand-${lo}-${hi}`;
}

export function getSkinHandAssetPath(skin: NonClassicTileSkin, a: number, b: number): string {
  const [lo, hi] = canonical(a, b);
  return `/Assets/game/tiles/${skin}/tiles_328x164/domino_${lo}-${hi}.png`;
}

export function getSkinPlacedTextureKey(skin: NonClassicTileSkin, a: number, b: number): string {
  const [lo, hi] = canonical(a, b);
  return `domino-${skin}-placed-${lo}-${hi}`;
}

export function getSkinPlacedAssetPath(skin: NonClassicTileSkin, a: number, b: number): string {
  const [lo, hi] = canonical(a, b);
  return `/Assets/game/tiles/${skin}/tiles_164x82/domino_${lo}-${hi}.png`;
}

export function getSkinBackTextureKey(skin: NonClassicTileSkin): string {
  return `domino-${skin}-back`;
}

export function getSkinBackAssetPath(skin: NonClassicTileSkin): string {
  return `/Assets/game/tiles/${skin}/tiles_328x164/domino_back.png`;
}

// ── Red-named aliases (kept for backward-compatible imports/tests) ─────────
export const getRedHandTextureKey = (a: number, b: number): string => getSkinHandTextureKey('red', a, b);
export const getRedHandAssetPath = (a: number, b: number): string => getSkinHandAssetPath('red', a, b);
export const getRedPlacedTextureKey = (a: number, b: number): string => getSkinPlacedTextureKey('red', a, b);
export const getRedPlacedAssetPath = (a: number, b: number): string => getSkinPlacedAssetPath('red', a, b);
export const getRedBackTextureKey = (): string => getSkinBackTextureKey('red');
export const getRedBackAssetPath = (): string => getSkinBackAssetPath('red');

// ── Viewer-skin-aware resolvers — the single integration point every ───────
// ── render call site should use instead of branching on skin itself.  ──────

/** Texture key for a face-up tile rendered in a "hand" role (rack, drag ghost, reveal art). */
export function getViewerHandTextureKey(skin: ViewerTileSkin, a: number, b: number): string {
  return skin === 'classic' ? getDominoTextureKey(a, b) : getSkinHandTextureKey(skin, a, b);
}

/** Public asset path for a face-up tile rendered in a "hand" role. Used in preload / React reveal art. */
export function getViewerHandAssetPath(skin: ViewerTileSkin, a: number, b: number): string {
  return skin === 'classic'
    ? `/Assets/game/tiles/tiles_328x164/domino_${Math.min(a, b)}-${Math.max(a, b)}.png`
    : getSkinHandAssetPath(skin, a, b);
}

/** Texture key for a face-up tile rendered in a "placed" (on-board) role. */
export function getViewerPlacedTextureKey(skin: ViewerTileSkin, a: number, b: number): string {
  return skin === 'classic' ? getDominoTextureKey(a, b) : getSkinPlacedTextureKey(skin, a, b);
}

/** Public asset path for a face-up tile rendered in a "placed" role. Used in preload only. */
export function getViewerPlacedAssetPath(skin: ViewerTileSkin, a: number, b: number): string {
  return skin === 'classic'
    ? `/Assets/game/tiles/tiles_328x164/domino_${Math.min(a, b)}-${Math.max(a, b)}.png`
    : getSkinPlacedAssetPath(skin, a, b);
}

/** Texture key for the face-down (back) tile. */
export function getViewerBackTextureKey(skin: ViewerTileSkin): string {
  return skin === 'classic' ? getDominoBackTextureKey() : getSkinBackTextureKey(skin);
}
